/**
 * stackLayout — how a pallet-direct stack is described to the shop floor.
 *
 * Twin of jobq-api `stack_layout.py`: same rules, same words, same cases
 * (`stackLayout.cases.json` is a copy of jobq-api `test_fixtures/stack_layout_cases.json`;
 * both test files check every case and jobq-api's test compares the two files).
 *
 * A pallet-direct zone lays its pieces on a tile_cols × tile_rows grid of spots; each spot is
 * one physical stack. When the quantity doesn't fill the top layer, the first spots (row-major)
 * get one more piece than the rest:
 *
 *   420 pcs on a 6 × 8 grid (48 spots) → "36 stacks × 9 high + 12 stacks × 8 high"
 *
 * Before 2026-10-16 the stack status label printed "9 layers × 46/layer" (420 ÷ 9, = 414),
 * the laser kiosk filled each spot to the maximum (46 full, 1 partial, 1 empty), the packing
 * plan said "36 cols × 9 layers + 12 cols × 8 layers" and the routing slip "36 stacks × 9 high
 * + 12 stacks × 8 high". Mike picked the slip's wording for all of them.
 *
 * Rules:
 *  - count in the zone's own units: finished assemblies for an assembly zone with
 *    primary_part_quantity > 1 (planned_qty is primary-part space), pieces otherwise;
 *  - heights come from the quantity, never the stored stack_height (it goes stale);
 *  - no tile grid (legacy zones) → spots = ceil(qty / stack_height);
 *  - fewer pieces than spots → "10 stacks × 1 high (2 spots empty)", never "× 0 high".
 */

export function stackUnits(plannedQty, assemblyId = null, primaryPartQuantity = null) {
  const qty = Math.trunc(Number(plannedQty) || 0);
  if (assemblyId != null) {
    const ppq = Math.trunc(Number(primaryPartQuantity) || 1);
    if (ppq > 1) return Math.floor(qty / ppq);
  }
  return qty;
}

export function stackLayout(qty, tileCols = null, tileRows = null, stackHeight = null) {
  qty = Math.trunc(Number(qty) || 0);
  if (qty <= 0) return null;
  let tc = Math.trunc(Number(tileCols) || 0);
  let tr = Math.trunc(Number(tileRows) || 0);
  let spots;
  if (tc > 0 && tr > 0) {
    spots = tc * tr;
  } else {
    tc = 0; tr = 0;
    const sh = Math.trunc(Number(stackHeight) || 0);
    spots = sh > 0 ? Math.max(1, Math.ceil(qty / sh)) : 1;
  }
  const full = Math.floor(qty / spots);
  const rem = qty - full * spots;
  let tallN, tallH, shortN, shortH;
  if (full === 0) {
    [tallN, tallH, shortN, shortH] = [rem, 1, 0, 0];
  } else if (rem) {
    [tallN, tallH, shortN, shortH] = [rem, full + 1, spots - rem, full];
  } else {
    [tallN, tallH, shortN, shortH] = [spots, full, 0, 0];
  }
  return {
    qty,
    spots,
    tile_cols: tc || null,
    tile_rows: tr || null,
    tall_n: tallN,
    tall_h: tallH,
    short_n: shortN,
    short_h: shortH,
    empty_n: spots - tallN - shortN,
    max_high: tallH,
  };
}

/** Target height of grid spot `idx` (row-major). 0 for an empty spot. */
export function cellHeight(layout, idx) {
  if (!layout) return 0;
  if (idx < layout.tall_n) return layout.tall_h;
  if (idx < layout.tall_n + layout.short_n) return layout.short_h;
  return 0;
}

/** "36 stacks × 9 high + 12 stacks × 8 high". asciiOnly for thermal labels. */
export function stackPhrase(layout, asciiOnly = false) {
  if (!layout) return '';
  const times = asciiOnly ? 'x' : '×';
  const part = (n, h) => `${n} stack${n === 1 ? '' : 's'} ${times} ${h} high`;
  let text = part(layout.tall_n, layout.tall_h);
  if (layout.short_n) text += ` + ${part(layout.short_n, layout.short_h)}`;
  if (layout.empty_n) {
    const e = layout.empty_n;
    text += ` (${e} spot${e === 1 ? '' : 's'} empty)`;
  }
  return text;
}

/** "1 pc" / "420 pcs" / "1 assembly" / "3 assemblies" (the number included). */
export function unitWord(n, assembly = false) {
  const k = Math.trunc(Number(n) || 0);
  if (assembly) return `${k} assembl${k === 1 ? 'y' : 'ies'}`;
  return `${k} pc${k === 1 ? '' : 's'}`;
}

/** Layout for a planned_box_zones row as the API returns it. */
export function zoneStackLayout(zone) {
  if (!zone) return null;
  const units = stackUnits(zone.planned_qty, zone.assembly_id, zone.primary_part_quantity);
  return stackLayout(units, zone.tile_cols, zone.tile_rows, zone.stack_height);
}

/**
 * Per-spot progress for a kiosk tile grid: spots fill one after another (spot 0 first), each to
 * its own target height, from `actualUnits`. Returns [{target, got}] for every spot.
 */
export function spotProgress(layout, actualUnits) {
  if (!layout) return [];
  let left = Math.max(0, Math.trunc(Number(actualUnits) || 0));
  const out = [];
  for (let i = 0; i < layout.spots; i += 1) {
    const target = cellHeight(layout, i);
    const got = Math.min(target, left);
    left -= got;
    out.push({ target, got });
  }
  return out;
}
